import { Box } from '@mui/material';
import React from 'react';

import { ResidentTasks } from 'types/dailyTasks';

import {
    LAUNDRY_COMPANY_CARE_PLAN_IDS,
    SHOWER_COMPANY_CARE_PLAN_IDS,
} from '@alliehealth/utils/dist/constants/care-plans.constants';
import { Selector } from 'components/Shared/Selector';
import { ResidentRowTask } from 'components/Shared/Task/TaskRow/ResidentRowTask';
import { pxToRem } from 'components/theme/typography';

import { AddedInfoActions } from './resident/AddedInfoActions';
import { BulkCompleteTray } from './resident/BulkCompleteTray';
import { CollapseIcon } from './resident/CollapseIcon';
import { ResidentDetails } from './resident/ResidentDetails';
import { ResidentPhoto } from './resident/ResidentPhoto';
import { TaskCounter } from './resident/TaskCounter';

type Props = {
    isOpen: boolean;
    isSelected: boolean;
    residentTasks: ResidentTasks;
    selectedTaskIds: Set<number>;
    taskIdCompletionOptions: { [key: number]: number };
    taskIdNotes: { [key: number]: string };
    onResidentToggle: (number) => void;
    onResidentSelectToggle: (number) => void;
    onResidentTaskSelectToggle: (number) => void;
    onToggleTaskNotes: (number) => void;
    onToggleEditAddTaskNotes: (number) => void;
    onToggleRejectDialog: (number) => void;
    onCompletionOptionsChange: (
        taskId: number,
        completionValue: number
    ) => void;
    onConfirmTask: (taskId: number, taskStatusId: number) => void;
    confirmSelectedTasks: (taskStatusId: number) => void;
    date: string;
};

export const ResidentRow = (props: Props) => {
    const {
        isOpen,
        isSelected,
        residentTasks,
        selectedTaskIds,
        taskIdCompletionOptions,
        taskIdNotes,
        onResidentToggle,
        onResidentSelectToggle,
        onResidentTaskSelectToggle,
        onToggleTaskNotes,
        onToggleEditAddTaskNotes,
        onToggleRejectDialog,
        onCompletionOptionsChange,
        onConfirmTask,
        confirmSelectedTasks,
        date,
    } = props;
    const { id, firstName, lastName, photo, roomNumber, isOnHospice, tasks } =
        residentTasks;

    const onToggleResident = () => onResidentToggle(id);
    const onSelectToggle = () => onResidentSelectToggle(id);

    const needsShower =
        tasks.find((task) =>
            new Set(SHOWER_COMPANY_CARE_PLAN_IDS).has(task.companyCarePlanId)
        ) !== undefined;

    const needsLaundry =
        tasks.find((task) =>
            new Set(LAUNDRY_COMPANY_CARE_PLAN_IDS).has(task.companyCarePlanId)
        ) !== undefined;

    const showBulkCompleteTray = isSelected && selectedTaskIds.size > 0;

    const areAllTasksSelected = tasks.length === selectedTaskIds.size;

    return (
        <Box
            sx={{
                borderBottom: 1,
                borderBottomColor: '#DEDEE0',
                paddingBottom: isOpen && showBulkCompleteTray ? 0 : pxToRem(16),
                paddingTop: pxToRem(16),
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: isOpen ? pxToRem(16) : 0,
                }}
            >
                <Selector
                    isSelected={isSelected}
                    onSelect={onSelectToggle}
                    areAllTasksSelected={areAllTasksSelected}
                    isResidentSelector
                />
                <ResidentPhoto
                    residentId={id}
                    firstName={firstName}
                    lastName={lastName}
                    photo={photo}
                />
                <Box
                    sx={{
                        display: 'flex',
                        flexGrow: 1,
                        alignItems: 'center',
                    }}
                    onClick={onToggleResident}
                >
                    <ResidentDetails
                        firstName={firstName}
                        lastName={lastName}
                        roomNumber={roomNumber}
                        needsShower={needsShower}
                        needsLaundry={needsLaundry}
                        isOnHospice={isOnHospice}
                    />
                    <TaskCounter count={tasks.length} />
                    <CollapseIcon isCollapsed={!isOpen} />
                </Box>
            </Box>
            {isOpen && <AddedInfoActions residentId={id} date={date} />}
            {isOpen &&
                tasks.map((task) => {
                    const onToggle = () =>
                        onResidentTaskSelectToggle(task.dailyTaskRecordId);
                    const onToggleNotes = () =>
                        onToggleTaskNotes(task.dailyTaskRecordId);
                    const onToggleEditNotes = () =>
                        onToggleEditAddTaskNotes(task.dailyTaskRecordId);
                    const onToggleReject = () =>
                        onToggleRejectDialog(task.dailyTaskRecordId);
                    const completionOptionValue =
                        taskIdCompletionOptions[task.dailyTaskRecordId];
                    const onConfirm = () =>
                        onConfirmTask(task.dailyTaskRecordId, 2);
                    return (
                        <ResidentRowTask
                            key={task.dailyTaskRecordId}
                            isSelected={selectedTaskIds.has(
                                task.dailyTaskRecordId
                            )}
                            onToggle={onToggle}
                            taskName={task.taskName}
                            taskTime={task.taskTime}
                            taskNotes={task.taskNotes}
                            userTaskNotes={taskIdNotes[task.dailyTaskRecordId]}
                            taskCompletionOptions={task.taskCompletionOptions}
                            onToggleNotes={onToggleNotes}
                            onToggleEditAddTaskNotes={onToggleEditNotes}
                            onToggleRejectDialog={onToggleReject}
                            onCompletionOptionsChange={(completionValue) =>
                                onCompletionOptionsChange(
                                    task.dailyTaskRecordId,
                                    completionValue
                                )
                            }
                            completionOptionValue={completionOptionValue}
                            onConfirmTask={onConfirm}
                        />
                    );
                })}
            {isOpen && showBulkCompleteTray && (
                <BulkCompleteTray
                    selectedTaskIds={[...selectedTaskIds]}
                    onToggleRejectDialog={onToggleRejectDialog}
                    confirmSelectedTasks={confirmSelectedTasks}
                />
            )}
        </Box>
    );
};
